import React, { useState } from 'react'
import { useClientMailboxReplyAnalytics, ClientMailboxReplyFilter } from '@/hooks/useClientMailboxReplyAnalytics'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { RefreshCw, TrendingUp, MessageCircle, Download, Clock, Building2, Calendar } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

interface ClientMailboxReplyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filter?: ClientMailboxReplyFilter
}

export function ClientMailboxReplyModal({ open, onOpenChange, filter }: ClientMailboxReplyModalProps) {
  const { data: replyData = [], isLoading, refetch } = useClientMailboxReplyAnalytics(filter)
  const [refreshing, setRefreshing] = useState(false)

  const handleRefresh = async () => {
    setRefreshing(true)
    await refetch()
    setRefreshing(false)
  }

  const formatLastReply = (timestamp: string | null) => {
    if (!timestamp) return 'Never'
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
      
      if (diffHours < 1) {
        return `${diffMins}m ago`
      } else if (diffHours < 24) {
        return `${diffHours}h ago`
      } else {
        return date.toLocaleDateString()
      }
    } catch {
      return 'Unknown'
    }
  }

  const exportToCsv = () => {
    const headers = ['Email', 'From Name', 'Client', 'Domain', 'Replies Today', 'Replies 7d', 'Replies 14d', 'Reply Rate', 'Last Reply', 'Total Replies']
    const csvData = replyData.map(row => [
      row.email,
      row.from_name,
      row.client_name,
      row.domain,
      row.replies_today,
      row.replies_7d,
      row.replies_14d,
      `${row.reply_rate.toFixed(2)}%`,
      formatLastReply(row.last_reply_time),
      row.total_replies
    ])
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filter?.clientName || 'client'}-mailbox-replies-${filter?.timePeriod || 'all'}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getTimePeriodLabel = (period: string) => {
    switch (period) {
      case 'today': return 'Today'
      case 'yesterday': return 'Yesterday'
      case 'last_7_days': return 'Last 7 Days'
      case 'last_14_days': return 'Last 14 Days'
      case 'last_30_days': return 'Last 30 Days'
      default: return period
    }
  }

  const getReplyCountForPeriod = (mailbox: any, period: string) => {
    switch (period) {
      case 'today': return mailbox.replies_today
      case 'yesterday': return Math.max(0, mailbox.replies_7d - mailbox.replies_today)
      case 'last_7_days': return mailbox.replies_7d
      case 'last_14_days': return mailbox.replies_14d
      case 'last_30_days': return mailbox.total_replies
      default: return 0
    }
  }

  const totalReplies = replyData.reduce((sum, item) => sum + getReplyCountForPeriod(item, filter?.timePeriod || 'today'), 0)
  const avgReplyRate = replyData.length > 0 
    ? replyData.reduce((sum, item) => sum + item.reply_rate, 0) / replyData.length 
    : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="space-y-1">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-success" />
              Mailbox Reply Details
            </DialogTitle>
            {filter && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span>{filter.clientName}</span>
                <span>•</span>
                <Calendar className="h-4 w-4" />
                <span>{getTimePeriodLabel(filter.timePeriod)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportToCsv}
              disabled={replyData.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </DialogHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Mailboxes with Replies
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-success">{replyData.length}</div>
              <p className="text-xs text-muted-foreground">
                {filter ? `in ${getTimePeriodLabel(filter.timePeriod)}` : 'total'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Replies</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">{totalReplies}</div>
              <p className="text-xs text-muted-foreground">
                {filter ? `for ${getTimePeriodLabel(filter.timePeriod)}` : 'all time'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Reply Rate</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">{avgReplyRate.toFixed(1)}%</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3 mr-1" />
                Across selected mailboxes
              </div>
            </CardContent>
          </Card>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : replyData.length === 0 ? (
          <Alert>
            <MessageCircle className="h-4 w-4" />
            <AlertDescription>
              {filter 
                ? `No mailboxes with replies found for ${filter.clientName} in ${getTimePeriodLabel(filter.timePeriod)}.`
                : 'No mailboxes with replies found.'
              }
            </AlertDescription>
          </Alert>
        ) : (
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>From Name</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead className="text-center">
                    {filter ? getTimePeriodLabel(filter.timePeriod) : 'Today'}
                  </TableHead>
                  <TableHead className="text-center">7 Days</TableHead>
                  <TableHead className="text-center">Rate</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead>Last Reply</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {replyData.map((mailbox) => (
                  <TableRow key={mailbox.email}>
                    <TableCell className="font-medium">
                      {mailbox.email}
                    </TableCell>
                    <TableCell>{mailbox.from_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {mailbox.domain}
                    </TableCell>
                    <TableCell className="text-center">
                      {getReplyCountForPeriod(mailbox, filter?.timePeriod || 'today') > 0 ? (
                        <Badge variant="default" className="bg-success text-success-foreground">
                          {getReplyCountForPeriod(mailbox, filter?.timePeriod || 'today')}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{mailbox.replies_7d}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {mailbox.reply_rate.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {mailbox.total_replies}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatLastReply(mailbox.last_reply_time)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}